import type { RedirectionRoute } from "../router-redirections";
import type { ResolverRoute } from "../router-resolver-gate";
import type { RouteObject } from "react-router";

import { Outlet, useRoutes } from "react-router";

import { ActivitiesList } from "@/features/Activities";
import Assets from "@/features/Assets";
import AssetTypes from "@/features/AssetTypes";
import Dashboard from "@/features/Dashboard";
import Import from "@/features/Import";
import Interventions from "@/features/Interventions";
import InterventionTemplates from "@/features/InterventionTemplates";
import Observations from "@/features/Observations";
import Organizations from "@/features/Organizations";
import Projects from "@/features/Projects";
import Users from "@/features/Users";
import UserItems from "@/features/Users/Items";
import { ActivityLog } from "@/modules/activity/activity-log";
import { AppLayout } from "@/modules/app-layout";
import { AssetTypePage } from "@/modules/asset-type/asset-type-page";
import { AssetTypeTemplatePage } from "@/modules/asset-type-template/asset-type-template-page";
import { AssetTypeTemplatesListPage } from "@/modules/asset-type-templates/asset-type-templates-list-page";
import { BatchPreviewPage } from "@/modules/batch-preview";
import { LocationPage } from "@/modules/location/location-page";
import LocationsListPage from "@/modules/locations/locations-list-page";
import { ObservationPage } from "@/modules/observation/observation-page";
import { PageError } from "@/modules/page-error";
import { PreferencesPage } from "@/modules/preferences-page";

import { ModalRouteGate } from "../router-modal-routes";
import { RouterRedirectionsGate } from "../router-redirections";
import { RouterResolverGate } from "../router-resolver-gate";

import { AppRouteGate } from "./router-app-routes.component";
import { useAppMainRoutesLocation } from "./router-app-routes.hook";

const AppModalRoutesHandler = () => {
  return useRoutes(APP_MODAL_ROUTES);
};

const AppMainRoutesHandler = () => {
  // All Main routes can be displayed as normal pages or as background
  // pages. Background pages only exist with modal pages.
  const controlledLocation = useAppMainRoutesLocation();
  return useRoutes(APP_MAIN_ROUTES, controlledLocation);
};

// Backend-facing resolver links: stable entrypoints that the FE resolves to current app routes.
export const APP_RESOLVER_LINKS: ResolverRoute[] = [
  {
    // Expected query params: ?context=:context&intervention_id=:intervention_id&observation_id=:observation_id&comment_id=:comment_id
    from: "/r/observation_comment",
    to: "/org/:context/interventions/:intervention_id?observation_id=:observation_id&sub_tab=comments",
  },
  {
    // Expected query params: ?context=:context&intervention_id=:intervention_id&job_module_id=:job_module_id
    from: "/r/job_module",
    to: "/org/:context/interventions/:intervention_id?tab=reports-modules",
  },
  {
    // Expected query params: ?context=:context&intervention_id=:intervention_id
    from: "/r/intervention",
    to: "/org/:context/interventions/:intervention_id",
  },
  {
    // Expected query params: ?context=:context&observation_id=:observation_id
    from: "/r/observation",
    to: "/org/:context/observations/:observation_id",
  },
  {
    // Expected query params: ?context=:context&project_id=:project_id
    from: "/r/project",
    to: "/org/:context/projects/:project_id",
  },
  {
    // Expected query params: ?context=:context&notification_type=:notification_type&user_name=:user_name&state=:state&ids=:ids
    from: "/r/transition_preview",
    to: "/org/:context/batch-preview?type=:notification_type&user_name=:user_name&state=:state&ids=:ids",
  },
  {
    // Expected query params: ?context=:context&notification_type=:notification_type&user_name=:user_name&ids=:ids
    from: "/r/assign_preview",
    to: "/org/:context/batch-preview?type=:notification_type&user_name=:user_name&ids=:ids",
  },
];

const APP_REDIRECTIONS: RedirectionRoute[] = [
  /** Home pages */
  { from: "/", to: "/org/:context" },
  { from: "/my-space", to: "/org/:context/my-space" },

  /** Locations */
  { from: "/locations", to: "/org/:context/locations" },
  { from: "/locations/:id", to: "/org/:context/locations/:id" },

  /** Assets */
  { from: "/assets", to: "/org/:context/assets" },
  { from: "/assets/:id", to: "/org/:context/assets/:id" },

  /** Interventions */
  { from: "/interventions", to: "/org/:context/interventions" },
  { from: "/interventions/:id", to: "/org/:context/interventions/:id" },

  /** Observations */
  { from: "/observations", to: "/org/:context/observations" },
  { from: "/observations/:id", to: "/org/:context/observations/:id" },

  /** Projects */
  { from: "/projects", to: "/org/:context/projects" },
  { from: "/projects/:id", to: "/org/:context/projects/:id" },

  /** Import */
  { from: "/import", to: "/admin/import" },

  /** Organizations */
  { from: "/organizations", to: "/admin/organizations" },
  { from: "/organizations/:id", to: "/admin/organizations" },

  /** Users */
  { from: "/users", to: "/admin/users" },
  { from: "/users/:id", to: "/admin/users/:id" },

  /** Asset types */
  { from: "/templates/assets", to: "/admin/asset-types" },
  { from: "/templates/assets/:id", to: "/admin/asset-types/:id" },
  { from: "/asset-types", to: "/admin/asset-types" },
  { from: "/asset-types/:id", to: "/admin/asset-types/:id" },

  /** Technical forms */
  { from: "/technical-forms", to: "/admin/technical-forms" },
  { from: "/technical-forms/:id", to: "/admin/technical-forms/:id" },

  /** Intervention templates */
  { from: "/templates/interventions", to: "/admin/intervention-templates" },
  {
    from: "/templates/interventions/:id",
    to: "/admin/intervention-templates/:id",
  },
  { from: "/intervention-templates", to: "/admin/intervention-templates" },
  {
    from: "/intervention-templates/:id",
    to: "/admin/intervention-templates/:id",
  },

  /** Activities */
  { from: "/activities", to: "/admin/activities" },
  { from: "/activities/:id", to: "/admin/activities/:id" },
];

export const APP_ROUTES: RouteObject[] = [
  {
    path: "/*",
    element: (
      <AppRouteGate>
        <RouterRedirectionsGate routes={APP_REDIRECTIONS}>
          <RouterResolverGate routes={APP_RESOLVER_LINKS}>
            {/* Keep order to make sure modal routes are working well */}
            <AppModalRoutesHandler />
            <AppMainRoutesHandler />
          </RouterResolverGate>
        </RouterRedirectionsGate>
      </AppRouteGate>
    ),
  },
];

const APP_MODAL_ROUTES: RouteObject[] = [
  {
    path: "org/:context",
    element: <Outlet />, // prevents "leaf route without element" warning when no child matches
    children: [
      {
        path: "observations/:id",
        element: (
          <ModalRouteGate parentRoute="/org/:context/observations">
            <ObservationPage />
          </ModalRouteGate>
        ),
      },
    ],
  },
];

const APP_MAIN_ROUTES: RouteObject[] = [
  {
    element: <AppLayout />,
    children: [
      {
        path: "org/:context",
        children: [
          { index: true, element: <Dashboard /> },
          { path: "my-space", element: <UserItems /> },
          {
            path: "locations",
            children: [
              { index: true, element: <LocationsListPage /> },
              { path: ":id", element: <LocationPage /> },
            ],
          },
          {
            path: "assets",
            children: [
              { index: true, element: <Assets.List /> },
              { path: ":id", element: <Assets.Details /> },
            ],
          },
          {
            path: "interventions",
            children: [
              { index: true, element: <Interventions.List /> },
              { path: ":id", element: <Interventions.Details /> },
            ],
          },
          { path: "observations", element: <Observations.List /> },
          { path: "observations/:id", element: null }, // modal route — prevents 404 flash before ModalRouteGate redirect
          {
            path: "projects",
            children: [
              { index: true, element: <Projects.List /> },
              { path: ":id", element: <Projects.Details /> },
            ],
          },
          {
            path: "batch-preview",
            element: <BatchPreviewPage />,
          },
        ],
      },
      {
        path: "admin",
        children: [
          { path: "import", element: <Import /> },
          {
            path: "organizations",
            children: [{ index: true, element: <Organizations.List /> }],
          },
          {
            path: "users",
            children: [
              { index: true, element: <Users.List /> },
              { path: ":id", element: <Users.Details /> },
            ],
          },
          {
            path: "asset-types",
            children: [
              { index: true, element: <AssetTypes.List /> },
              { path: ":id", element: <AssetTypePage /> },
            ],
          },
          {
            path: "technical-forms",
            children: [
              { index: true, element: <AssetTypeTemplatesListPage /> },
              { path: ":id", element: <AssetTypeTemplatePage /> },
            ],
          },
          {
            path: "intervention-templates",
            children: [
              { index: true, element: <InterventionTemplates.List /> },
              { path: ":id", element: <InterventionTemplates.Details /> },
            ],
          },
          {
            path: "activities",
            children: [
              { index: true, element: <ActivitiesList /> },
              { path: ":id", element: <ActivityLog /> },
            ],
          },
        ],
      },
      { path: "preferences", element: <PreferencesPage /> },
      { path: "*", element: <PageError status={404} /> },
    ],
  },
];
